import React, { useCallback, useEffect, useState } from "react";
import { AlertCircle, Banknote, CheckCircle2, ExternalLink, FileText, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { Panel, Skeleton } from "./ui";
import { api } from "../lib/api";
import { money, shortDate } from "../lib/format";
import { useTranslation } from "react-i18next";

/**
 * Getting paid: tax declaration, Stripe onboarding, and the payout button.
 *
 * The tax status is a radio with no default. Kleinunternehmer and VAT-registered are
 * genuinely different — one adds 19% to the payout and the other must not — and
 * pre-selecting either would mean a wrong answer becomes the common one. So it stays
 * unset until the investor picks, and the payout refuses to run before then.
 *
 * The declaration is a draft with a Save button, following rule 9: it decides how much
 * money leaves the company and what a tax document says, which is not something to
 * write on blur.
 */
const TAX_STATUSES = ["kleinunternehmer", "vat_registered"];

export default function PayoutSetup() {
  const { t } = useTranslation();
  const [state, setState] = useState(null);
  const [draft, setDraft] = useState(null);
  const [saving, setSaving] = useState(false);
  const [busy, setBusy] = useState(false);

  const load = useCallback(() => {
    api.payoutSetup().then((r) => {
      setState(r.data);
      setDraft({ ...r.data.profile, self_billing_agreed: !!r.data.profile.self_billing_agreed_at });
    }).catch(() => setState({ configured: false }));
  }, []);
  useEffect(() => { load(); }, [load]);

  const dirty = draft && state && (
    draft.tax_status !== state.profile.tax_status ||
    (draft.legal_name || "") !== (state.profile.legal_name || "") ||
    (draft.address || "") !== (state.profile.address || "") ||
    (draft.tax_number || "") !== (state.profile.tax_number || "") ||
    (draft.vat_id || "") !== (state.profile.vat_id || "") ||
    !!draft.self_billing_agreed !== !!state.profile.self_billing_agreed_at
  );

  const save = async () => {
    if (!dirty || saving) return;
    setSaving(true);
    try {
      await api.savePayoutProfile({
        tax_status: draft.tax_status,
        legal_name: draft.legal_name || "",
        address: draft.address || "",
        tax_number: draft.tax_number || "",
        vat_id: draft.vat_id || "",
        self_billing_agreed: !!draft.self_billing_agreed,
      });
      toast.success(t("dialogs.saved"));
      load();
    } catch (e) { toast.error(e.response?.data?.detail || t("dialogs.failed")); }
    finally { setSaving(false); }
  };

  const onboard = async () => {
    setBusy(true);
    try {
      const { data } = await api.payoutOnboard();
      window.location.href = data.url;
    } catch (e) {
      toast.error(e.response?.data?.detail || t("dialogs.failed"));
      setBusy(false);
    }
  };

  const requestPayout = async () => {
    if (!window.confirm(t("payouts.confirmRequest", {
      amount: money(state.balance.total_eur, "EUR") }))) return;
    setBusy(true);
    try {
      const { data } = await api.requestPayout();
      toast.success(t("payouts.requested", { number: data.number }));
      load();
    } catch (e) { toast.error(e.response?.data?.detail || t("dialogs.failed")); }
    finally { setBusy(false); }
  };

  if (!state) return <Skeleton className="h-64" />;
  if (!state.configured) {
    return (
      <Panel className="card p-5 sm:p-6" testid="payout-setup">
        <h2 className="font-heading font-semibold tracking-tight text-lg">{t("payouts.title")}</h2>
        <p className="text-sm text-slate-500 mt-2">{t("payouts.notConfigured")}</p>
      </Panel>
    );
  }

  const { profile, balance } = state;
  const vatSelected = draft?.tax_status === "vat_registered";

  return (
    <Panel className="card p-5 sm:p-6 space-y-6" testid="payout-setup">
      <div>
        <div className="flex items-center gap-2 mb-1">
          <Banknote size={17} className="text-brand-500" />
          <h2 className="font-heading font-semibold tracking-tight text-lg">{t("payouts.title")}</h2>
        </div>
        <p className="text-sm text-slate-500 leading-relaxed">{t("payouts.body")}</p>
      </div>

      {/* Balance first — it is what the investor came to see. */}
      <div className="rounded-2xl bg-slate-50 p-4" data-testid="payout-balance">
        <div className="flex items-baseline justify-between gap-3">
          <span className="text-sm text-slate-500">{t("payouts.available")}</span>
          <span className="text-2xl font-heading font-semibold tabular">
            {money(balance.net_eur, "EUR")}
          </span>
        </div>
        {balance.vat_eur > 0 && (
          <div className="mt-2 pt-2 border-t border-slate-200/70 text-xs text-slate-500 space-y-1 tabular">
            <div className="flex justify-between">
              <span>{t("payouts.plusVat", { pct: balance.vat_pct })}</span>
              <span>{money(balance.vat_eur, "EUR")}</span>
            </div>
            <div className="flex justify-between font-medium text-slate-700">
              <span>{t("payouts.youReceive")}</span>
              <span>{money(balance.total_eur, "EUR")}</span>
            </div>
          </div>
        )}
        <p className="text-[11px] text-slate-400 mt-2">
          {t("payouts.minNote", { min: money(balance.min_payout_eur, "EUR") })}
        </p>
      </div>

      {/* Tax status. No default — see the component docstring. */}
      <div className="space-y-3">
        <span className="label block">{t("payouts.taxStatus")}</span>
        {TAX_STATUSES.map((key) => (
          <label key={key} className={`flex items-start gap-3 p-3.5 rounded-xl border cursor-pointer transition-colors ${
            draft?.tax_status === key ? "border-brand-300 bg-brand-50/50" : "border-slate-200 hover:bg-slate-50"}`}>
            <input type="radio" name="tax_status" className="mt-1" data-testid={`tax-${key}`}
                   checked={draft?.tax_status === key}
                   onChange={() => setDraft((d) => ({ ...d, tax_status: key }))} />
            <span className="text-sm">
              <span className="font-medium text-slate-800 block">{t(`payouts.tax.${key}`)}</span>
              <span className="text-slate-500 leading-relaxed">{t(`payouts.taxDesc.${key}`)}</span>
            </span>
          </label>
        ))}
      </div>

      <div className="grid sm:grid-cols-2 gap-4">
        <label className="block">
          <span className="label block mb-1.5">{t("payouts.legalName")}</span>
          <input className="input" data-testid="payout-legal-name" value={draft?.legal_name || ""}
                 onChange={(e) => setDraft((d) => ({ ...d, legal_name: e.target.value }))} />
        </label>
        <label className="block">
          <span className="label block mb-1.5">
            {vatSelected ? t("payouts.vatId") : t("payouts.taxNumber")}
          </span>
          <input className="input" data-testid="payout-vat-id"
                 value={(vatSelected ? draft?.vat_id : draft?.tax_number) || ""}
                 onChange={(e) => setDraft((d) => ({
                   ...d, [vatSelected ? "vat_id" : "tax_number"]: e.target.value }))} />
        </label>
        <label className="block sm:col-span-2">
          <span className="label block mb-1.5">{t("payouts.address")}</span>
          <textarea rows={3} className="input" data-testid="payout-address"
                    value={draft?.address || ""}
                    onChange={(e) => setDraft((d) => ({ ...d, address: e.target.value }))} />
        </label>
      </div>

      {/* The consent that makes a self-billed document lawful (§14 Abs. 2 UStG). */}
      <label className="flex items-start gap-2.5 text-sm cursor-pointer">
        <input type="checkbox" className="mt-0.5" data-testid="payout-selfbilling"
               checked={!!draft?.self_billing_agreed}
               onChange={(e) => setDraft((d) => ({ ...d, self_billing_agreed: e.target.checked }))} />
        <span className="text-slate-600 leading-relaxed">{t("payouts.selfBilling")}</span>
      </label>

      <div className="flex items-center gap-3 border-t border-slate-100 pt-4">
        <button className="btn-primary" data-testid="payout-save" disabled={!dirty || saving}
                onClick={save}>
          {saving ? <Loader2 size={15} className="animate-spin" /> : null}
          {t("payouts.save")}
        </button>
        <button className="btn-ghost text-sm" data-testid="payout-discard" disabled={!dirty || saving}
                onClick={() => setDraft({ ...profile,
                                          self_billing_agreed: !!profile.self_billing_agreed_at })}>
          {t("payouts.discard")}
        </button>
        {dirty && <span className="text-xs text-amber-600 font-medium">{t("payouts.unsaved")}</span>}
      </div>

      {/* Stripe. Bank details and identity live there, never here. */}
      <div className="rounded-xl border border-slate-200 p-4" data-testid="payout-stripe">
        <div className="flex items-start gap-3">
          {profile.payouts_enabled
            ? <CheckCircle2 size={18} className="text-emerald-500 shrink-0 mt-0.5" />
            : <AlertCircle size={18} className="text-amber-500 shrink-0 mt-0.5" />}
          <div className="min-w-0 flex-1">
            <div className="font-medium text-sm">
              {profile.payouts_enabled ? t("payouts.stripeReady") : t("payouts.stripeNeeded")}
            </div>
            <p className="text-sm text-slate-500 mt-1 leading-relaxed">{t("payouts.stripeBody")}</p>
          </div>
        </div>
        <button className="btn-outline mt-3 w-full sm:w-auto justify-center" data-testid="payout-onboard"
                onClick={onboard} disabled={busy}>
          {busy ? <Loader2 size={15} className="animate-spin" /> : <ExternalLink size={15} />}
          {profile.details_submitted ? t("payouts.updateStripe") : t("payouts.setUpStripe")}
        </button>
      </div>

      <button className="btn-primary w-full justify-center" data-testid="payout-request"
              onClick={requestPayout} disabled={busy || !balance.can_request}>
        {busy ? <Loader2 size={16} className="animate-spin" /> : <Banknote size={16} />}
        {t("payouts.requestNow", { amount: money(balance.total_eur, "EUR") })}
      </button>
      {!balance.can_request && balance.missing?.length > 0 && (
        <p className="text-xs text-amber-600 -mt-3" data-testid="payout-missing">
          {t("payouts.stillNeeded")} {balance.missing.map((m) => t(`payouts.missing.${m}`)).join(", ")}
        </p>
      )}

      {state.documents?.length > 0 && (
        <div className="border-t border-slate-100 pt-4">
          <h3 className="font-medium text-sm mb-2 flex items-center gap-2">
            <FileText size={15} className="text-slate-400" /> {t("payouts.documents")}
          </h3>
          <div className="divide-y divide-slate-50">
            {state.documents.map((d) => (
              <div key={d.number} className="py-2.5 flex items-center justify-between text-sm gap-3"
                   data-testid={`gutschrift-${d.number}`}>
                <div className="min-w-0">
                  <div className="font-medium tabular truncate">{d.number}</div>
                  <div className="text-xs text-slate-400">{shortDate(d.issued_at)}</div>
                </div>
                <span className="tabular font-medium">{money(d.total_eur, "EUR")}</span>
              </div>
            ))}
          </div>
        </div>
      )}
    </Panel>
  );
}
